// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { placeHolders } from "@awc/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import saveCurrentAccount from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/SaveCurrentAccount";
import updateAccountSettings from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/UpdateAccountSettings";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateAccountSettings,
    saveCurrentAccount,
};

const mapStateToProps = (state: ApplicationState) => ({
    editingAccount: state.pages.accountSettings.editingAccount,
    emailErrorMessage: state.pages.accountSettings.emailErrorMessage,
    useEmailAsUserName: getSettingsCollection(state).accountSettings.useEmailAsUserName,
    changePasswordUrl: getPageLinkByPageType(state, "ChangePasswordPage")?.url,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountSettingsUserInformationStyles {
    userInformationTitle: TypographyProps;
    userInformationGridItem?: GridItemProps;
    changePasswordLink?: LinkPresentationProps;
    // XNG-Changes
    container?: InjectableCss;
    titleWrapper?: InjectableCss;
    inputsWrapper?: InjectableCss;
    passwordWrapper?: InjectableCss;
    textValueWrapper?: InjectableCss;
    text?: TypographyProps;
    value?: TypographyProps;
    emailWrapper?: InjectableCss;
    changeEmailModal?: ModalPresentationProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
    emailTextField?: TextFieldPresentationProps;
}

export const userInformationStyles: AccountSettingsUserInformationStyles = {
    userInformationGridItem: {
        width: 12,
    },
    changePasswordLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    userInformationTitle: {
        variant: "h2",
    },
};

const styles = userInformationStyles;
const password = "*************";
// XNG-Changes
const formId = "accountSettingsForm";

const AccountSettingsUserInformation = ({
    editingAccount,
    useEmailAsUserName,
    changePasswordUrl,
    emailErrorMessage,
    updateAccountSettings,
    // XNG-Changes
    saveCurrentAccount,
}: Props) => {
    // <XNG-Changes>
    const [isChangeEmailModalIsOpen, setIsChangeEmailModalIsOpen] = useState(false);
    const [newEmail, setNewEmail] = useState<string>("");
    const [newEmailErrorMessage, setNewEmailErrorMessage] = useState<string>("");
    const toasterContext = useContext(ToasterContext);

    useEffect(() => {
        if (editingAccount) {
            setNewEmail(editingAccount?.email);
        }
    }, [editingAccount]);
    // </XNG-Changes>

    if (!editingAccount) {
        return null;
    }

    const fullName = `${editingAccount.firstName} ${editingAccount.lastName}`;
    const emailChangeHandler = () => {
        // XNG-Changes:change to match classic

        // updateAccountSettings({ email: event.currentTarget.value });
        updateAccountSettings({ email: newEmail });

        saveCurrentAccount({
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Settings updated"), messageType: "success" });
            },
            onError: (message: any) => {
                toasterContext.addToast({ body: message, messageType: "warning" });
            },
        });
        setIsChangeEmailModalIsOpen(false);
    };

    const validateNewEmail = (email: string) => {
        if (typeof email === "undefined") {
            return;
        }

        const requiredFieldMessage = siteMessage("Field_Required", translate("Email Address"));
        const emailFieldMessage = siteMessage("Field_Invalid", translate("Email Address"));

        const errorMessage = email === "" ? requiredFieldMessage : validateEmail(email) ? "" : emailFieldMessage;
        setNewEmailErrorMessage(errorMessage as string);
    };

    // XNG-Changes
    // const label = (
    //     <>
    //         <Typography>{translate("Password")}</Typography>
    //         <Link {...styles.changePasswordLink} href={changePasswordUrl}>
    //             {translate("Change")}
    //         </Link>
    //     </>
    // );

    // XNG-Changes:use StyledWrapper instead of GridContainer to match the design
    return (
        <StyledWrapper {...styles.container}>
            {/* <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.userInformationTitle}>{translate("Your Information")}</Typography>
            </StyledWrapper> */}
            <StyledWrapper {...styles.inputsWrapper}>
                {/* {!useEmailAsUserName && (
                    <TextField
                        label={translate("User Name")}
                        value={editingAccount.userName}
                        data-test-selector="accountSettings_userName"
                        disabled
                    />
                )} */}
                <StyledWrapper {...styles.textValueWrapper}>
                    <Typography {...styles.text}>{translate("User Name")}</Typography>
                    <Typography {...styles.value}>{editingAccount.userName}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.textValueWrapper}>
                    <Typography {...styles.text}>{translate("Email")}</Typography>
                    <StyledWrapper {...styles.emailWrapper}>
                        <Typography {...styles.value}>{editingAccount.email}</Typography>
                        <Link
                            {...styles.changePasswordLink}
                            onClick={() => {
                                setIsChangeEmailModalIsOpen(true);
                            }}
                        >
                            {translate("Change email")}
                        </Link>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.textValueWrapper}>
                    <Typography {...styles.text}>{translate("Full Name")}</Typography>
                    <Typography {...styles.value}>{fullName}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.textValueWrapper}>
                    <Typography {...styles.text}>{translate("Password")}</Typography>
                    <Link {...styles.changePasswordLink} href={changePasswordUrl}>
                        {translate("Change password")}
                    </Link>
                </StyledWrapper>
                {/* <TextField
                    // XNG-Changes: remove type to prevent custom validation since it is handled from OOTB (to fix ootb bug)
                    type="text"
                    label={translate("Email")}
                    onChange={emailChangeHandler}
                    value={editingAccount.email}
                    data-test-selector="accountSettings_email"
                    required
                    maxLength={256}
                    error={emailErrorMessage}
                    // Xng-Changes
                    placeholder={placeHolders.email}
                    {...{ formId, showError: !!emailErrorMessage }}
                /> */}
                {/* <TextField label={translate("Full Name")} value={fullName} disabled />
                <StyledWrapper {...styles.passwordWrapper}>
                    <TextField type="password" label={translate("Password")} disabled value={password} />
                    <Link {...styles.changePasswordLink} href={changePasswordUrl}>
                        {translate("Change Password")}
                    </Link>
                </StyledWrapper> */}
            </StyledWrapper>
            <Modal
                headline={translate("Change Email Address")}
                {...styles.changeEmailModal}
                isOpen={isChangeEmailModalIsOpen}
                handleClose={() => {
                    setIsChangeEmailModalIsOpen(false);
                }}
            >
                <TextField
                    type="text"
                    label={translate("Email")}
                    onChange={(e: any) => {
                        setNewEmail(e.currentTarget.value);
                        validateNewEmail(e.currentTarget.value);
                    }}
                    value={newEmail}
                    data-test-selector="accountSettings_email"
                    required
                    maxLength={256}
                    error={newEmailErrorMessage}
                    placeholder={placeHolders.email}
                    labelPosition="left"
                    {...{ formId, showError: !!newEmailErrorMessage }}
                    {...styles.emailTextField}
                />
                <StyledWrapper {...styles.buttonsWrapper}>
                    <Button
                        {...styles.cancelButton}
                        onClick={() => {
                            setNewEmail(editingAccount.email);
                            setIsChangeEmailModalIsOpen(false);
                            setNewEmailErrorMessage("");
                        }}
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button {...styles.saveButton} disabled={!!newEmailErrorMessage} onClick={emailChangeHandler}>
                        {translate("Save")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsUserInformation),
    definition: {
        allowedContexts: ["AccountSettingsPage"],
        group: "Account Settings",
        displayName: "User Information",
    },
};

export default widgetModule;
